import { Injectable, inject } from '@angular/core';

import { FixtureBase, FixtureType } from '@cds/betting-offer';
import { FixtureAddons, FixturePricingState } from '@cds/betting-offer/add-ons';
import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { RacingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { OptionMarketsFixture, ParticipantsFixture, WinGameFixture, WinGameFixturePreview } from '@frontend/sports/racing/feature/models';
import { RaceStatuses } from '@frontend/sports/types/components/sitecore';
import { IntlService } from '@frontend/vanilla/core';

import { getDayOfRace } from '../helpers';

export interface PricingStateFixture extends Pick<FixtureBase, 'fixtureType'> {
    addons: Pick<FixtureAddons, 'pricingState'>;
}

export function updateStatus(fixture: WinGameFixturePreview, sitecore: Sitecore): string {
    if (fixture.addons.raceSummary) {
        const raceStatus = Object.getOwnPropertyNames(sitecore.horseRacing.raceStatuses).find(
            (o) => o.toUpperCase() === fixture.addons.raceSummary.toUpperCase().split(' ').join(''),
        );

        return raceStatus && sitecore.horseRacing.raceStatuses[<keyof RaceStatuses>raceStatus]
            ? sitecore.horseRacing.raceStatuses[<keyof RaceStatuses>raceStatus]
            : fixture.addons.raceSummary;
    } else {
        return '';
    }
}

@Injectable({
    providedIn: 'root',
})
export class HorsesFixtureHelper {
    private sitecore = inject(Sitecore);
    private intlService = inject(IntlService);
    private racingConfig = inject(RacingConfig);

    static isTradable(fixture: PricingStateFixture): boolean {
        return (
            fixture.fixtureType !== FixtureType.DayOfRace ||
            fixture.addons.pricingState === FixturePricingState.None ||
            fixture.addons.pricingState === FixturePricingState.Priced
        );
    }

    filterSelectedMarketOptionGroup(fixture: WinGameFixture, selectedMarketId: number): EventOptionGroup[] {
        return fixture.optionGroups.filter((optionGroup) => optionGroup.detailedGrouping.marketSet === selectedMarketId);
    }

    updateStatus(fixture: WinGameFixture): string {
        if (fixture.addons.raceSummary) {
            const raceStatus = Object.getOwnPropertyNames(this.sitecore.horseRacing.raceStatuses).find(
                (o) => o.toUpperCase() === fixture.addons.raceSummary.toUpperCase().replaceAll(' ', ''),
            );

            return raceStatus ? this.sitecore.horseRacing.raceStatuses[raceStatus as keyof RaceStatuses] : '';
        } else {
            return '';
        }
    }

    checkIfBOGApplicable(fixture: WinGameFixture | OptionMarketsFixture | ParticipantsFixture): boolean | undefined {
        const day = getDayOfRace(fixture.startDate);
        if (day === DayOfRaceDate.Tomorrow || this.intlService.formatDate(fixture.startDate, 'HH:mm') <= this.racingConfig.bogTodayThresholdTime) {
            return false;
        }

        return fixture.addons.bestOddsGuarantee;
    }
}
